/**
 * Pure utilities for schedule block creation.
 * Used for slot generation and date handling.
 */

import type { KnockoutStart } from '@/utils/tournament-schedule';
import { getDayOfWeek } from '@/types/schedule.model';
import type { ScheduleEntry, ScheduleTimeRange } from '@/types/schedule.model';
import type { DateRange } from 'react-day-picker';

export interface TimeSlot {
  startTime: string;
  endTime: string;
}

/** Map: date -> startTime -> court count */
export type TimeSlotsByDate = Record<string, Record<string, number>>;

const DEFAULT_SLOT_INTERVAL_MINUTES = 30;
const DAY_START_MINUTES = 8 * 60; // 08:00
const DAY_END_MINUTES = 23 * 60; // 23:00

/** Allowed intervals (in minutes) between the start of each block. */
export const SLOT_INTERVAL_OPTIONS = [15, 30] as const;

/**
 * Generate time slots: matches can start every `intervalMinutes` (e.g. 30 → 08:00, 08:30, 09:00…).
 * Each slot has duration `minutes` (e.g. 75 min → 08:00-09:15, 08:30-09:45…).
 * All start times that fit before 23:00 are generated.
 */
export function generateSlotsForDuration(
  minutes: number,
  intervalMinutes: number = DEFAULT_SLOT_INTERVAL_MINUTES
): TimeSlot[] {
  if (minutes < 15 || minutes > 180) return [];
  if (intervalMinutes !== 15 && intervalMinutes !== 30) {
    intervalMinutes = DEFAULT_SLOT_INTERVAL_MINUTES;
  }

  const slots: TimeSlot[] = [];
  const pad = (n: number) => n.toString().padStart(2, '0');

  for (let startMin = DAY_START_MINUTES; startMin + minutes <= DAY_END_MINUTES; startMin += intervalMinutes) {
    const endMin = startMin + minutes;
    slots.push({
      startTime: `${pad(Math.floor(startMin / 60))}:${pad(startMin % 60)}`,
      endTime: `${pad(Math.floor(endMin / 60))}:${pad(endMin % 60)}`,
    });
  }
  return slots;
}

/**
 * Get list of date strings (YYYY-MM-DD) between from and to (inclusive).
 */
export function getDatesInRange(from: Date, to: Date): string[] {
  const dates: string[] = [];
  const current = new Date(from);
  current.setHours(0, 0, 0, 0);
  const end = new Date(to);
  end.setHours(0, 0, 0, 0);

  while (current <= end) {
    dates.push(current.toISOString().slice(0, 10));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

/** Format date for display: "jue. 20 feb." */
export function formatDayLabel(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('es-AR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/** Format short: "20 feb" */
export function formatDayShort(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'short',
  });
}

/** Build API ScheduleEntry[] from timeSlotsByDate and slot definitions. */
export function buildScheduleEntries(
  timeSlotsByDate: TimeSlotsByDate,
  slots: TimeSlot[],
): ScheduleEntry[] {
  const entries: ScheduleEntry[] = [];

  for (const [dateKey, byTime] of Object.entries(timeSlotsByDate)) {
    const timeRanges: ScheduleTimeRange[] = [];

    for (const [timeKey, courtCount] of Object.entries(byTime)) {
      const slot = slots.find((s) => s.startTime === timeKey);
      if (slot && courtCount > 0) {
        timeRanges.push({
          qty: courtCount,
          courtsQuantity: courtCount,
          startTime: slot.startTime,
          endTime: slot.endTime,
        });
      }
    }

    if (timeRanges.length > 0) {
      entries.push({
        date: dateKey,
        dayOfWeek: getDayOfWeek(new Date(dateKey + 'T00:00:00')),
        timeRange: timeRanges,
      });
    }
  }

  return entries;
}

/**
 * Fills `to` with `from` so a single calendar click already yields a usable
 * one-day range. react-day-picker returns `{ from, to: undefined }` on the
 * first click, which consumers treat as "nothing selected".
 */
export function normalizeDateRange(
  range: DateRange | undefined,
): DateRange | undefined {
  if (!range?.from) return undefined;
  if (!range.to) return { from: range.from, to: range.from };
  return range;
}

/**
 * Where the knockout bracket goes while building a block. LAST_DAY and GROUPS_ONLY save no cut, so
 * the bracket keeps the whole last day of the tournament as it always did; GROUPS_ONLY only drops
 * that day from the preview, for the groups block of a tournament whose bracket has its own block.
 */
export type KnockoutMode = 'LAST_DAY' | 'FROM_SLOT' | 'WHOLE_BLOCK' | 'GROUPS_ONLY';

/** Start times of a day that have courts, in order. */
export function startTimesWithCourts(timeSlotsByDate: TimeSlotsByDate, date: string): string[] {
  return Object.entries(timeSlotsByDate[date] ?? {})
    .filter(([, courts]) => courts > 0)
    .map(([startTime]) => startTime)
    .sort();
}

/**
 * Turns the chosen mode into a concrete slot of the grid. Null for the modes that save no cut, and
 * when the chosen slot has no courts.
 */
export function resolveKnockoutStart(
  mode: KnockoutMode,
  slot: KnockoutStart | null,
  timeSlotsByDate: TimeSlotsByDate,
  days: string[],
): KnockoutStart | null {
  if (mode === 'WHOLE_BLOCK') {
    for (const date of [...days].sort()) {
      const earliest = startTimesWithCourts(timeSlotsByDate, date)[0];
      if (earliest) return { date, startTime: earliest };
    }
    return null;
  }
  if (mode !== 'FROM_SLOT' || !slot || !days.includes(slot.date)) return null;
  return (timeSlotsByDate[slot.date]?.[slot.startTime] ?? 0) > 0 ? slot : null;
}
